import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { RelatedChannels } from "@/components/calv/RelatedChannels";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/en/services/performance-marketing/affiliate-marketing")({
  head: () => {
    const h = pageHead("pmAffiliateMarketing", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Affiliate Marketing Agency",
          description: "Network setup, partner acquisition, tracking hygiene, and commission management — managed for economic contribution, not affiliate reach.",
          url: "/en/services/performance-marketing/affiliate-marketing",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Performance Marketing", path: "/en/services/performance-marketing" },
          { name: "Affiliate Marketing", path: "/en/services/performance-marketing/affiliate-marketing" },
        ])),
      ],
    };
  },
  component: AffiliateEN,
});

const steps = [
  { num: "01", title: "Analysis & Publisher Mix Check (3–5 days)", body: "Account audit (if affiliate activity is already running), publisher mix analysis (which types contribute, which don't), tracking setup check, commission model audit, competitive analysis in affiliate market. Output: concrete hypotheses plus realistic expectation management." },
  { num: "02", title: "Setup, Tracking Hardening & Network Selection (1–2 weeks)", body: "Hardening affiliate tracking server-side (postback setup, conversion validation). Network selection depending on mandate profile. Commission model definition with new-customer differentiation. Establishing brand-bidding bans and last-click discipline." },
  { num: "03", title: "Publisher Acquisition & Activation (from week 2–3)", body: "Targeted acquisition of content publishers, comparison portals, and loyalty programs fitting the client. Sorting existing publishers by economic contribution — increase for high performers, reduction or shutoff for last-click takers." },
  { num: "04", title: "Scaling & Ongoing Operations (continuous)", body: "Monthly publisher performance review. Commission model adjustments depending on market development. Cross-channel validation (affiliate vs. search-brand cannibalization). Operational management typically directly by the managing director." },
];

const capabilities = [
  { name: "Network Selection & Setup", detail: "Awin · financeAds · Belboon" },
  { name: "Server-side Tracking", detail: "Postbacks · conversion validation" },
  { name: "Publisher Acquisition", detail: "Content · comparison · loyalty" },
  { name: "Commission Management", detail: "New-customer differentiation" },
  { name: "Brand-Bidding Protection", detail: "Monitoring · sanctions" },
  { name: "Cookie-Window Discipline", detail: "Real conversion cycles" },
  { name: "Cross-Channel Attribution", detail: "Integrated funnel context" },
  { name: "Reporting & Management", detail: "Active scaling · shutdown" },
];

const audience = [
  { tag: "Primary", title: "Established B2C E-commerce with scaling need", body: "Affiliate marketing works particularly well for established e-commerce setups with strong brand position that can use content publishers as recommendation amplifiers. Sensible from monthly revenues of around €100,000, fully scalable." },
  { tag: "Secondary", title: "Products with high comparability", body: "Affiliate works particularly well where consumers are in research phase — insurance, credit cards, travel, electronics, software. Comparison portals and content affiliates are the right partner types." },
  { tag: "Tertiary", title: "Brands with loyalty program potential", body: "Where cashback and loyalty programs fit the business model — fashion, telecommunications, DTC subscriptions. But caution: not every cashback affiliate delivers economic contribution." },
  { tag: "Disqualification", title: "When we're not the right partner", body: "For monthly revenues under €50,000. When affiliate is seen as a set-and-forget channel. For pure B2B or SaaS setups." },
];

const realistic = [
  "Commission efficiency improvement of 20–40% in the first year is realistic when previously working with unsorted publisher mix and without last-click discipline.",
  "New customer share of affiliate revenue increasable to 40–60% through differentiated commission models — many setups have a new-customer share below 20%.",
  "Affiliate volume leverage of 30–50% through active acquisition of new content publishers and comparison portals is a typical range.",
  "Brand-bidding protection saves 5–15% affiliate commissions — money that previously flowed to affiliates contributing nothing because the customer would have been found via brand search anyway.",
];

const value = [
  ["Active publisher management instead of set-and-forget.", "We report on economic contribution per publisher type, not on affiliate reach."],
  ["Tracking hygiene as mandatory component.", "Server-side postbacks, conversion validation, cookie-window discipline."],
  ["Network-agnostic approach.", "We recommend the network fitting the mandate — not the one with which we make the highest volume bonuses."],
  ["Brand-bidding protection systematically.", "Clear rules plus monitoring plus sanctions."],
  ["Operational management by experienced hand.", "Directly by the managing director in most mandates."],
  ["Compensation model without volume coupling.", "We don't earn more when affiliate volume grows."],
];

const related = [
  { to: "/en/services/performance-marketing", name: "Performance Marketing", sub: "Affiliate as part of integrated performance mix" },
  { to: "/en/services/tracking-data-foundation", name: "Tracking & Data Foundation", sub: "Clean attribution as mandatory prerequisite" },
  { to: "/en/services/strategic-coordination", name: "Strategic Coordination", sub: "Cross-channel strategy including affiliate shares" },
];

const channelLinks = [
  { to: "/en/services/performance-marketing/google-ads", name: "Google Ads", sub: "Search, Shopping, PMax and Demand Gen — performance on Google's owned surfaces." },
  { to: "/en/services/performance-marketing/meta-ads", name: "Advertising on Facebook & Instagram", sub: "Performance on Instagram and Facebook with creative discipline." },
];

function AffiliateEN() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Performance Marketing · Affiliate Marketing"
          headline={<>Affiliate Marketing Agency — <em>Partner Steering Instead of Commission Lottery,</em> with Tracking Discipline.</>}
          lead="Network setup, partner acquisition, tracking hygiene, and commission management — managed for economic contribution, not for affiliate reach. Calvarius works as an affiliate marketing agency for e-commerce mandates that understand affiliate as a performance channel — not as a set-and-forget voucher channel."
          metaStrip={["B2C Focus", "Network-agnostic", "Tracking Hygiene"]}
          breadcrumbs={[
            { label: "Home", to: "/en" },
            { label: "Services", to: "/en/services" },
            { label: "Performance Marketing", to: "/en/services/performance-marketing" },
            { label: "Affiliate Marketing" },
          ]}
        />

        <Section number="02" kicker="Position" headline={<>What an Affiliate Marketing Agency with <em>discipline</em> delivers today.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Anyone looking for an affiliate marketing agency beyond standard network administrators has typically made a critical observation: affiliate marketing is poorly managed in many setups. Commissions flow to publishers who deliver little economic contribution (last-click cookie stuffing, brand-bidding affiliates, cashback providers entering at funnel endgame). Cookie lifetimes and attribution models disadvantage top-of-funnel publishers. The actual performance potential of the channel — qualified recommendations through content publishers and comparison portals — often remains unused.</p>
              <p className="mb-5">Calvarius treats affiliate marketing as a performance channel with discipline on two levels. First: <strong className="font-semibold text-ink">partner steering</strong> — we deliberately activate publisher types that deliver economic contribution and systematically sort out publishers who only siphon last-click commissions. Second: <strong className="font-semibold text-ink">tracking discipline</strong> — affiliate tracking is technically complex, error-prone, and poorly set up in many setups.</p>
              <p>Concretely: we work network-agnostically — Awin, financeAds, Belboon, or direct in-house tracking, depending on mandate needs. We set commission models that reward economic behavior. We track via server-side postbacks plus first-party data validation. And we manage the affiliate mix as part of the integrated performance setup — via our <Link to="/en/services/strategic-coordination" className="underline">Strategic Coordination discipline</Link>.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Audience Fit" headline={<>When you need an Affiliate Marketing Agency <em>— and when you don't.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {audience.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Methodology" headline={<>How our Affiliate Marketing Agency <em>starts mandates.</em></>}>
          <ProcessFlow steps={steps} />
          <RevealOnScroll>
            <div className="mt-10 max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7">
              <p className="eyebrow mb-3">Fast start as competitive advantage</p>
              <p className="text-[15px] leading-relaxed text-ink-2">While classic affiliate agencies often need six to twelve weeks before the first optimized setup with active publisher management runs, we are productive within two to three weeks. Possible through network-agnostic approach and established partner relationships.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="What we deliver" headline={<>What our Affiliate Marketing Agency <em>concretely delivers.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="06" kicker="Tooling" headline={<>Tools and stack of our <em>affiliate management.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Network Platforms</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Awin (international, broad)</li>
                  <li>financeAds (DACH financial sector)</li>
                  <li>Belboon (DACH e-commerce)</li>
                  <li>Tradedoubler (international)</li>
                  <li>Direct tracking via in-house setups</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-6">
                <p className="eyebrow mb-4">Tracking & Analytics</p>
                <ul className="space-y-2 text-sm text-ink-2">
                  <li>Server-side postbacks via GTM Server</li>
                  <li>GA4 for cross-channel validation</li>
                  <li>Looker Studio publisher dashboards</li>
                  <li>Brand-bidding monitoring tools</li>
                </ul>
              </div>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="What's realistic" headline={<>Realistic results of <em>affiliate marketing optimization.</em></>}
          caption="Affiliate is a discipline channel — anyone not actively managing systematically loses.">
          <RevealOnScroll>
            <ul className="space-y-4">
              {realistic.map((r, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-6">
                  <span className="font-mono text-xs text-cyan-deep">{String(i+1).padStart(2,"0")}</span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{r}</p>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="What you gain" headline={<>What you get from our <em>Affiliate Marketing Agency.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {value.map(([h, b]) => (
                <div key={h} className="bg-white p-7">
                  <div className="font-display text-lg text-ink">{h}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedChannels locale="en" items={channelLinks} />

        <Section variant="tinted" number="09" kicker="Go deeper" headline={<>Related disciplines.</>}
          caption="These topics work in interplay.">
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} className="group flex flex-col rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-lime hover:shadow-[0_12px_40px_-20px_oklch(0.85_0.18_125/0.5)]">
                  <h3 className="font-display text-xl font-semibold text-ink">{r.name}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-3">{r.sub}</p>
                  <span aria-hidden className="mt-5 font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-lime-deep">→</span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Link to="/en/services/performance-marketing" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">
                ← All performance marketing disciplines
              </Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="10" kicker="Get concrete">
          <CTABlock
            headline={<>Affiliate marketing audit in 30 minutes — <em>free and non-binding.</em></>}
            body="In a first conversation, we clarify whether affiliate marketing makes economic sense in your setup — and if so, with what publisher mix strategy. You get an honest assessment."
            primary={{ label: "Schedule a conversation", to: "/en/contact" }}
            secondary={{ label: "Performance marketing overview", to: "/en/services/performance-marketing" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
